import React from "react"
import moment from "moment"
import api from "src/api"

const getDefaultFilters = () => [
  {
    t: "mOrE",
    f: "delivery_date",
    v: moment().subtract(1, "day").format("YYYY-MM-DD HH:mm:ss"),
  },
]

export function useGetData() {
  const [dataItems, setDataItems] = React.useState([])
  const [summary, setSummary] = React.useState(null)
  const [paymentAccounts, setPaymentAccounts] = React.useState([])
  const [points, setPoints] = React.useState([])
  const [isLoading, setIsLoading] = React.useState(false)
  const [pagination, setPagination] = React.useState(null)
  const [filters, setFilters] = React.useState(getDefaultFilters)
  const [withDiscrepancies, setWithDiscrepancies] = React.useState(false)
  const [requiresAttention, setRequiresAttention] = React.useState(false)
  const [sortBy, setSortBy] = React.useState(null)
  const [sortDir, setSortDir] = React.useState(null)

  React.useEffect(() => {
    getItems()
  }, [])

  const buildFilterParams = (currentFilters) =>
    currentFilters
      .map(
        (f, i) =>
          `filters[${i}][t]=${encodeURIComponent(f.t)}&filters[${i}][f]=${encodeURIComponent(f.f)}&filters[${i}][v]=${encodeURIComponent(f.v)}`,
      )
      .join("&")

  const buildQuery = (
    page,
    countOnPage,
    currentFilters,
    currentWithDiscrepancies,
    currentRequiresAttention,
    currentSortBy,
    currentSortDir,
  ) => {
    const filterParams = currentFilters.length > 0 ? `&${buildFilterParams(currentFilters)}` : ""
    const discrepanciesParam = currentWithDiscrepancies ? "&withDiscrepancies=1" : ""
    const attentionParam = currentRequiresAttention ? "&requiresAttention=1" : ""
    const sortByParam = currentSortBy ? `&sortBy=${encodeURIComponent(currentSortBy)}` : ""
    const sortDirParam = currentSortDir ? `&sortDir=${encodeURIComponent(currentSortDir)}` : ""
    return `page=${page}&countOnPage=${countOnPage}${filterParams}${discrepanciesParam}${attentionParam}${sortByParam}${sortDirParam}`
  }

  const getItems = (
    page = 1,
    countOnPage = 20,
    currentFilters = filters,
    currentWithDiscrepancies = withDiscrepancies,
    currentRequiresAttention = requiresAttention,
    currentSortBy = sortBy,
    currentSortDir = sortDir,
  ) => {
    setIsLoading(true)
    const query = buildQuery(
      page,
      countOnPage,
      currentFilters,
      currentWithDiscrepancies,
      currentRequiresAttention,
      currentSortBy,
      currentSortDir,
    )

    api
      .get(`receipts?${query}`, { useV2Api: true })
      .then(({ data }) => {
        setPagination({
          current: data.pagination.currentPage,
          pageSize: data.pagination.countOnPage,
          total: data.pagination.total,
        })
        setDataItems(data.items ?? [])
        setSummary(data.summary ?? null)
        setPaymentAccounts(data.paymentAccounts ?? [])
        setPoints(data.points ?? [])
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  function handleTableChange(pag, _tableFilters, sorter) {
    let nextSortBy = null
    let nextSortDir = null
    if (sorter && sorter.order && sorter.field) {
      nextSortBy = sorter.field
      nextSortDir = sorter.order === "ascend" ? "asc" : "desc"
    }
    setSortBy(nextSortBy)
    setSortDir(nextSortDir)
    getItems(pag.current, pag.pageSize, filters, withDiscrepancies, requiresAttention, nextSortBy, nextSortDir)
  }

  function handleSearch(
    newFilters,
    newWithDiscrepancies = withDiscrepancies,
    newRequiresAttention = requiresAttention,
  ) {
    setFilters(newFilters)
    setWithDiscrepancies(newWithDiscrepancies)
    setRequiresAttention(newRequiresAttention)
    getItems(1, 20, newFilters, newWithDiscrepancies, newRequiresAttention, sortBy, sortDir)
  }

  function getCurrentExportQuery() {
    const filterParams = filters.length > 0 ? buildFilterParams(filters) : ""
    const discrepanciesParam = withDiscrepancies ? "withDiscrepancies=1" : ""
    const attentionParam = requiresAttention ? "requiresAttention=1" : ""
    const sortByParam = sortBy ? `sortBy=${encodeURIComponent(sortBy)}` : ""
    const sortDirParam = sortDir ? `sortDir=${encodeURIComponent(sortDir)}` : ""
    return [filterParams, discrepanciesParam, attentionParam, sortByParam, sortDirParam]
      .filter(Boolean)
      .join("&")
  }

  return {
    dataItems,
    summary,
    paymentAccounts,
    points,
    pagination,
    isLoading,
    filters,
    withDiscrepancies,
    requiresAttention,
    handleTableChange,
    handleSearch,
    getCurrentExportQuery,
    refetch: () => getItems(),
  }
}
